// Mercapoli - Panel Comercio: Menú en Línea / Widget (Fase 4.1 + Analytics 4.2)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Code, Copy, Check, ExternalLink, Globe, Palette,
  Smartphone, Monitor, QrCode, Share2, Loader2, Eye, RefreshCw,
  MessageCircle, Link2, Settings, Shield, Plus, Trash2, Save,
  BarChart3, TrendingUp, MousePointer, ShoppingCart, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { comercio as comercioApi } from '../../lib/api';

const MerchantMenuWidget = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Datos del comercio
  const [merchantId, setMerchantId] = useState('');
  const [merchantData, setMerchantData] = useState(null);
  
  // Config del widget
  const [theme, setTheme] = useState('light');
  const [lang, setLang] = useState('es');
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  
  // Códigos generados
  const [embedCodes, setEmbedCodes] = useState(null);
  const [copied, setCopied] = useState(null);
  
  // Preview
  const [showPreview, setShowPreview] = useState(false);
  
  // Analytics (Fase 4.2)
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadMerchantData();
  }, []);

  useEffect(() => {
    if (merchantId) {
      loadEmbedCodes();
    }
  }, [merchantId, theme, lang]);

  const loadMerchantData = async () => {
    try {
      // Obtener datos del dashboard del comercio que incluye el restaurante_id
      const dashData = await comercioApi.getDashboard();
      if (dashData?.restaurante_id) {
        setMerchantId(dashData.restaurante_id);
        setMerchantData(dashData);
        setAllowedDomains(dashData.widget_allowed_domains || []);
      } else {
        toast.error('No se encontró el comercio vinculado');
      }
    } catch (error) {
      console.error('Error loading merchant data:', error);
      toast.error('Error al cargar datos del comercio');
    }
    setLoading(false);
  };

  const loadEmbedCodes = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/widget/embed-code/${merchantId}?theme=${theme}&lang=${lang}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('mercapoli_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setEmbedCodes(data);
      }
    } catch (error) {
      console.error('Error loading embed codes:', error);
    }
  };

  const loadAnalytics = async () => {
    if (!merchantId) return;
    
    setAnalyticsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/widget/analytics/${merchantId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('mercapoli_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
    setAnalyticsLoading(false);
  };

  // Cargar analytics cuando se tiene merchantId
  useEffect(() => {
    if (merchantId) {
      loadAnalytics();
    }
  }, [merchantId]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/widget/settings/${merchantId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('mercapoli_token')}`
        },
        body: JSON.stringify({
          allowed_domains: allowedDomains
        })
      });
      
      if (response.ok) {
        toast.success('Configuración guardada');
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
    setSaving(false);
  };

  const addDomain = () => {
    if (!newDomain.trim()) return;
    
    const domain = newDomain.trim().toLowerCase()
      .replace('http://', '')
      .replace('https://', '')
      .split('/')[0];
    
    if (allowedDomains.includes(domain)) {
      toast.error('Este dominio ya está agregado');
      return;
    }
    
    setAllowedDomains([...allowedDomains, domain]);
    setNewDomain('');
    toast.success('Dominio agregado');
  };

  const removeDomain = (domain) => {
    setAllowedDomains(allowedDomains.filter(d => d !== domain));
    toast.success('Dominio eliminado');
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success('Código copiado al portapapeles');
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const shareWhatsApp = () => {
    if (!embedCodes?.embed_codes?.whatsapp_message) return;
    
    const message = encodeURIComponent(embedCodes.embed_codes.whatsapp_message);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/merchant')} 
                className="p-2 -ml-2 rounded-lg hover:bg-muted"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="font-heading text-lg font-bold">Menú en Línea</h1>
                <p className="text-xs text-muted-foreground">Widget embebible para tu sitio web</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => window.open(embedCodes?.preview_url, '_blank')}
              >
                <Eye className="h-4 w-4 mr-2" />
                Vista Previa
              </Button>
              <Button onClick={saveSettings} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6 space-y-6">
        {/* Introducción */}
        <Card className="bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 border-brand-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-brand-primary rounded-xl">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">¡Lleva tu menú a cualquier sitio web!</h2>
                <p className="text-muted-foreground">
                  Copia el código y pégalo en tu sitio web, WordPress, Wix, o cualquier página. 
                  Tus clientes podrán ver tu menú y ordenar directamente, sin salir de tu sitio.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuración del Widget */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Opciones */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Personalización
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Tema</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger data-testid="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">☀️ Claro</SelectItem>
                    <SelectItem value="dark">🌙 Oscuro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Idioma</Label>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger data-testid="lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">🇪🇸 Español</SelectItem>
                    <SelectItem value="en">🇺🇸 English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Seguridad */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Dominios Permitidos
              </CardTitle>
              <CardDescription>
                Configura qué sitios web pueden mostrar tu menú. 
                Deja vacío para permitir cualquier dominio.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="ej: mirestaurante.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                />
                <Button onClick={addDomain} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar
                </Button>
              </div>
              
              {allowedDomains.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {allowedDomains.map((domain) => (
                    <Badge 
                      key={domain} 
                      variant="secondary"
                      className="flex items-center gap-2 py-1.5 px-3"
                    >
                      <Globe className="h-3 w-3" />
                      {domain}
                      <button
                        onClick={() => removeDomain(domain)}
                        className="ml-1 hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Sin restricciones. El widget puede mostrarse en cualquier sitio.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Códigos de Embebido */}
        <Tabs defaultValue="script" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="script" className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              Script
            </TabsTrigger>
            <TabsTrigger value="iframe" className="flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              iFrame
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="qr" className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              QR Code
            </TabsTrigger>
          </TabsList>

          {/* Script */}
          <TabsContent value="script" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Código Script (Recomendado)</CardTitle>
                <CardDescription>
                  El método más flexible. Copia y pega este código donde quieras mostrar tu menú.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-sm">
                    <code>{embedCodes?.embed_codes?.script || 'Cargando...'}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(embedCodes?.embed_codes?.script, 'script')}
                  >
                    {copied === 'script' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  💡 <strong>Tip:</strong> Funciona en WordPress, Wix, Shopify, HTML puro, y cualquier sitio web.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* iFrame */}
          <TabsContent value="iframe" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Código iFrame</CardTitle>
                <CardDescription>
                  Alternativa simple si el script no funciona en tu plataforma.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="p-4 bg-muted rounded-lg overflow-x-auto text-sm">
                    <code>{embedCodes?.embed_codes?.iframe || 'Cargando...'}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(embedCodes?.embed_codes?.iframe, 'iframe')}
                  >
                    {copied === 'iframe' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp */}
          <TabsContent value="whatsapp" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-green-500" />
                  Enlace para WhatsApp
                </CardTitle>
                <CardDescription>
                  Comparte tu menú directamente por WhatsApp.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 whitespace-pre-wrap">
                    {embedCodes?.embed_codes?.whatsapp_message}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(embedCodes?.embed_codes?.whatsapp_link, 'whatsapp_link')}
                    className="flex-1"
                  >
                    {copied === 'whatsapp_link' ? (
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                    ) : (
                      <Link2 className="h-4 w-4 mr-2" />
                    )}
                    Copiar enlace
                  </Button>
                  <Button 
                    onClick={shareWhatsApp}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Compartir en WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Code */}
          <TabsContent value="qr" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  Código QR
                </CardTitle>
                <CardDescription>
                  Imprime este QR y colócalo en tu local. Los clientes pueden escanear para ver tu menú.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="inline-block p-4 bg-white rounded-xl shadow-lg">
                  {/* QR Code generado con API pública */}
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(embedCodes?.embed_codes?.whatsapp_link || '')}`}
                    alt="QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Escanea con la cámara del celular para ver tu menú
                </p>
                <Button variant="outline" onClick={() => {
                  const url = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(embedCodes?.embed_codes?.whatsapp_link || '')}`;
                  window.open(url, '_blank');
                }}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Descargar QR (Alta resolución)
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Vista Previa */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Vista Previa
                </CardTitle>
                <CardDescription>
                  Así se verá tu menú en otros sitios web
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? 'Ocultar' : 'Mostrar'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(embedCodes?.preview_url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Abrir en nueva pestaña
                </Button>
              </div>
            </div>
          </CardHeader>
          {showPreview && (
            <CardContent>
              <div 
                className="border rounded-xl overflow-hidden"
                style={{ height: '600px' }}
              >
                <iframe
                  src={`${API_URL}/menu/${merchantId}?embed=true&theme=${theme}&lang=${lang}`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Menu Preview"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Analytics del Widget (Fase 4.2) */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Analytics del Widget
                </CardTitle>
                <CardDescription>
                  Métricas de tu menú embebido este mes
                </CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadAnalytics}
                disabled={analyticsLoading}
              >
                <RefreshCw className={`h-4 w-4 ${analyticsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {analytics ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <Eye className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                  <p className="text-2xl font-bold">{analytics.summary?.total_views || 0}</p>
                  <p className="text-sm text-muted-foreground">Vistas del menú</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <MousePointer className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                  <p className="text-2xl font-bold">{analytics.summary?.product_clicks || 0}</p>
                  <p className="text-sm text-muted-foreground">Clicks en productos</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <ShoppingCart className="h-6 w-6 mx-auto mb-2 text-green-500" />
                  <p className="text-2xl font-bold">{analytics.summary?.add_to_cart || 0}</p>
                  <p className="text-sm text-muted-foreground">Agregados al carrito</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <TrendingUp className="h-6 w-6 mx-auto mb-2 text-orange-500" />
                  <p className="text-2xl font-bold">{analytics.summary?.checkout_starts || 0}</p>
                  <p className="text-sm text-muted-foreground">Checkouts iniciados</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {analyticsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                ) : (
                  <p>No hay datos de analytics aún. Embede tu menú para comenzar a trackear.</p>
                )}
              </div>
            )}
            
            {analytics?.summary?.conversion_rate > 0 && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-green-700 dark:text-green-400">
                      Tasa de conversión
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Porcentaje de vistas que inician checkout
                    </p>
                  </div>
                  <Badge className="bg-green-500 text-white text-lg px-3 py-1">
                    {analytics.summary.conversion_rate}%
                  </Badge>
                </div>
              </div>
            )}
            
            {analytics?.top_domains?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold mb-2">Top dominios con tu menú</h4>
                <div className="space-y-2">
                  {analytics.top_domains.slice(0, 5).map((domain, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        {domain.domain}
                      </span>
                      <Badge variant="secondary">{domain.views} vistas</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instrucciones */}
        <Card>
          <CardHeader>
            <CardTitle>📖 Instrucciones de Instalación</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-bold mb-2">WordPress</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Edita la página donde quieres el menú</li>
                  <li>Agrega un bloque "HTML personalizado"</li>
                  <li>Pega el código Script</li>
                  <li>Guarda y publica</li>
                </ol>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-bold mb-2">Wix</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Abre el editor de tu sitio</li>
                  <li>Agrega "Embed HTML" desde el menú</li>
                  <li>Selecciona "Código" y pega el iFrame</li>
                  <li>Ajusta el tamaño y publica</li>
                </ol>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-bold mb-2">HTML / Otros</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Abre el archivo HTML de tu página</li>
                  <li>Pega el código Script dentro del body</li>
                  <li>Guarda y sube los cambios</li>
                  <li>¡Listo!</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MerchantMenuWidget;
